import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Mail, Clock, RefreshCw, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getOverdueClass = (days) => {
  if (days > 30) return 'bg-red-100 text-red-700';
  if (days > 14) return 'bg-amber-100 text-amber-700';
  return 'bg-yellow-100 text-yellow-700';
};

export default function OverdueInvoicesModule({ authHeaders, refreshStats }) {
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [reminderForm, setReminderForm] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const fetchOverdue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/crm/invoices/overdue`, { headers: authHeaders });
      setOverdue(res.data);
    } catch (err) {
      setOverdue([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchOverdue(); }, [fetchOverdue]);

  const sendReminder = async (inv) => {
    if (!inv.client_email) {
      alert("Ce client n'a pas d'adresse email renseignée.");
      return;
    }
    setSending(inv.id);
    try {
      await axios.post(`${API_URL}/api/crm/invoices/${inv.id}/remind`, {
        recipient_email: inv.client_email,
        recipient_name: inv.client_name,
        message: customMessage
      }, { headers: authHeaders });
      alert(`Relance envoyée à ${inv.client_email}`);
      setReminderForm(null);
      setCustomMessage('');
      fetchOverdue();
      refreshStats?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(null);
    }
  };

  const totalOverdue = overdue.reduce((s, inv) => s + (inv.remaining || 0), 0);

  return (
    <div data-testid="overdue-invoices-module">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
            <AlertTriangle size={16} />
            <span className="text-sm font-semibold">{overdue.length} facture{overdue.length > 1 ? 's' : ''} en retard</span>
          </div>
          {totalOverdue > 0 && (
            <span className="text-sm text-gray-500">
              Total impayé : <span className="font-bold text-red-600">{totalOverdue.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</span>
            </span>
          )}
        </div>
        <button onClick={fetchOverdue} className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50" data-testid="overdue-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      ) : overdue.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Clock size={40} className="mx-auto text-green-300 mb-3" />
          <p className="text-green-600 font-medium">Aucune facture en retard !</p>
          <p className="text-gray-400 text-sm mt-1">Toutes les factures sont à jour.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">N° Facture</th>
                  <th className="text-left p-3">Client</th>
                  <th className="text-right p-3">Montant dû</th>
                  <th className="text-center p-3">Retard</th>
                  <th className="text-left p-3 hidden md:table-cell">Échéance</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {overdue.map(inv => (
                  <tr key={inv.id} className="border-t hover:bg-gray-50" data-testid={`overdue-row-${inv.id}`}>
                    <td className="p-3 font-mono font-medium">{inv.number}</td>
                    <td className="p-3">
                      <p className="text-gray-900">{inv.client_name}</p>
                      {inv.client_email && <p className="text-xs text-gray-500">{inv.client_email}</p>}
                    </td>
                    <td className="p-3 text-right font-bold text-red-600">{inv.remaining?.toFixed(2)}€</td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getOverdueClass(inv.days_overdue)}`}>
                        {inv.days_overdue}j
                      </span>
                    </td>
                    <td className="p-3 hidden md:table-cell text-gray-500">{inv.due_date?.slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => { setReminderForm(inv); setCustomMessage(''); }}
                        disabled={sending === inv.id || !inv.client_email}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 ml-auto"
                        data-testid={`remind-btn-${inv.id}`}
                      >
                        {sending === inv.id ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                        Relancer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reminder Modal */}
      {reminderForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reminder-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <h3 className="font-semibold mb-1">Relance de paiement</h3>
            <p className="text-sm text-gray-500 mb-4">
              Facture <strong>{reminderForm.number}</strong> — {reminderForm.client_name}
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Destinataire</label>
                <input
                  type="text"
                  value={reminderForm.client_email || 'Pas d\'email'}
                  disabled
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Montant dû</label>
                <div className="text-lg font-bold text-red-600">{reminderForm.remaining?.toFixed(2)}€</div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Message personnalisé (optionnel)</label>
                <textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Ajouter un message personnalisé..."
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="reminder-message"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setReminderForm(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                  Annuler
                </button>
                <button
                  onClick={() => sendReminder(reminderForm)}
                  disabled={!reminderForm.client_email || sending}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid="send-reminder-btn"
                >
                  {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                  Envoyer la relance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
